import React, { useMemo } from 'react';
import { type useScrollspy } from '@sg/garnish';
import {
  type CustomizationMachineActorRef,
  type CustomizationMachineTypes,
  useCustomizationContext,
  useCustomizationHelpers,
} from '@sg/shared-fe/features/customization';

import { CustomizationIngredientsGrid } from '@order/features/customization';

import { CustomizationModificationsGroupCard } from '../../../../components';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationModificationGroup = (props: Props) => {
  const {
    actor,
    modificationGroup,
    activeStepperId,
    resetStepperTimeout,
    setActiveStepperId,
    scrollSpy,
  } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { addIngredient, removeIngredient } = useCustomizationHelpers({
    actor,
  });
  const {
    modificationLimits,
    maxModificationsCount,
    activeModificationsMap,
    initialModificationsMap,
  } = useCustomizationContext({ actor });

  // ─── Derived Data ─────────────────────────────────────────────────────

  const modifications = useMemo(() => {
    return [...modificationGroup.modifications].sort(
      (modification, nextModification) => {
        const isModificationIsInitial = initialModificationsMap.has(modification.id); // prettier-ignore
        const isNextModificationIsInitial = initialModificationsMap.has(nextModification.id); // prettier-ignore

        if (isModificationIsInitial && !isNextModificationIsInitial) return -1;

        if (!isModificationIsInitial && isNextModificationIsInitial) return 1;

        return 0;
      },
    );
  }, [modificationGroup.modifications, initialModificationsMap]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <CustomizationIngredientsGrid.Section
      key={modificationGroup.id}
      sectionId={modificationGroup.id}
      ingredientKindName={modificationGroup.displayName}
      register={scrollSpy.helpers.target.register}
      deregister={scrollSpy.helpers.target.deregister}
    >
      <CustomizationIngredientsGrid.SectionStack>
        {modifications.map((modification) => (
          <CustomizationModificationsGroupCard
            key={modification.id}
            activeModificationsMap={activeModificationsMap}
            activeStepperId={activeStepperId}
            groupLimits={modificationLimits?.[modificationGroup.name]}
            maxModificationsCount={maxModificationsCount}
            modification={modification}
            onIncrement={addIngredient}
            onDecrement={removeIngredient}
            resetStepperTimeout={resetStepperTimeout}
            setActiveStepperId={setActiveStepperId}
          />
        ))}
      </CustomizationIngredientsGrid.SectionStack>
    </CustomizationIngredientsGrid.Section>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  actor: CustomizationMachineActorRef;
  modificationGroup: CustomizationMachineTypes.ProductModifierGroup;
  activeStepperId: string | null;
  resetStepperTimeout: () => void;
  setActiveStepperId: (id: string | null) => void;
  scrollSpy: ReturnType<typeof useScrollspy>;
};
